// Operator › System agents (SYSTEM-AGENT, founder 2026-10-29): the platform's own agents in each
// granted company (HTVA's Skillsmith) — never shown to the company. One row per agent with its
// status and report count; a row opens its latest reports (`GET /v1/operator/system-agents`, one
// call). Reports are stored platform-side, readable by operators only.
import {
  Badge,
  EmptyState,
  InfoTip,
  LocationHeader,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  WorkerBadge,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronDownIcon, ChevronRightIcon, CpuIcon } from "lucide-react";
import { Fragment, useState } from "react";
import type { Api } from "../../api/client";
import { operatorSystemAgentsQuery } from "../ops-queries";
import { OpsBody, OpsFrame, operatorCrumb, RowSub, TABLE_ROW_HOVER } from "../ops-ux";
import { LoadError, Loading } from "../screen-parts";
import { SEVERITY_BADGE, SEVERITY_LABEL } from "./agent-health";
import { SystemAgentKeyDialog } from "./system-agent-key-dialog";

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export function SystemAgentsPage({ api }: { api: Api }) {
  const q = useQuery(operatorSystemAgentsQuery(api));
  const [open, setOpen] = useState<string | null>(null);
  const rows = q.data?.data ?? [];
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[operatorCrumb, { label: "System agents" }]}
        icon={CpuIcon}
        title="System agents"
        count={q.isPending ? undefined : rows.length}
        actions={
          <InfoTip label="About system agents">
            The platform's own agents in a company. Nobody in the company sees them or can talk to
            them; they read runs, verdicts, corrections and skill usage and report here only.
          </InfoTip>
        }
      />
      <OpsBody>
        {q.isPending ? (
          <Loading label="Loading system agents" />
        ) : q.isError ? (
          <LoadError what="System agents" error={q.error} />
        ) : rows.length === 0 ? (
          <EmptyState
            variant="card"
            icon={CpuIcon}
            title="No system agents"
            description="A DE converted into a system agent appears here."
          />
        ) : (
          <Table data-slot="operator-system-agents">
            <TableCaption>
              One row per system agent; open a row for its latest reports.
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">System agent</TableHead>
                <TableHead scope="col">Status</TableHead>
                <TableHead scope="col" className="text-right">
                  Reports
                </TableHead>
                <TableHead scope="col">Latest report</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const key = `${r.company.id}:${r.membership_id}`;
                const expanded = open === key;
                const latest = r.reports[0];
                return (
                  <Fragment key={key}>
                    <TableRow data-system-agent={r.membership_id} className={TABLE_ROW_HOVER}>
                      <TableHead scope="row" className="font-normal">
                        <button
                          type="button"
                          aria-expanded={expanded}
                          onClick={() => setOpen(expanded ? null : key)}
                          className="inline-flex items-center gap-1 text-left font-medium"
                        >
                          {expanded ? (
                            <ChevronDownIcon className="size-4" aria-hidden="true" />
                          ) : (
                            <ChevronRightIcon className="size-4" aria-hidden="true" />
                          )}
                          <WorkerBadge kind="system" size="sm" />
                          {r.name}
                        </button>
                        <RowSub>
                          {r.company.name}
                          {r.can_talk ? (
                            <>
                              {" · "}
                              <Link
                                to="/operator/agent-health/$companyId"
                                params={{ companyId: r.company.id }}
                                className="underline-offset-4 hover:underline"
                              >
                                Open conversation
                              </Link>
                            </>
                          ) : null}
                        </RowSub>
                        {r.can_talk ? (
                          <SystemAgentKeyDialog
                            api={api}
                            companyId={r.company.id}
                            membershipId={r.membership_id}
                            name={r.name}
                          />
                        ) : null}
                      </TableHead>
                      <TableCell>
                        <Badge variant={r.status === "active" ? "success" : "neutral"}>
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{r.reports.length}</TableCell>
                      <TableCell>
                        {latest ? (
                          <div className="flex flex-col">
                            <span>{latest.title}</span>
                            <RowSub>{when(latest.created_at)}</RowSub>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">No reports yet</span>
                        )}
                      </TableCell>
                    </TableRow>
                    {expanded ? (
                      <TableRow data-slot="system-agent-reports">
                        <TableCell colSpan={4}>
                          {r.reports.length === 0 ? (
                            <span className="text-muted-foreground">No reports yet</span>
                          ) : (
                            <ul className="flex flex-col gap-3">
                              {r.reports.map((rep) => (
                                <li key={rep.id} className="flex flex-col gap-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Badge variant={SEVERITY_BADGE[rep.severity]}>
                                      {SEVERITY_LABEL[rep.severity]}
                                    </Badge>
                                    <Badge variant="outline">{rep.kind}</Badge>
                                    <span className="font-medium">{rep.title}</span>
                                    <RowSub>{when(rep.created_at)}</RowSub>
                                  </div>
                                  <p className="whitespace-pre-wrap text-sm">{rep.body}</p>
                                  {rep.evidence.length ? (
                                    <RowSub>
                                      Evidence:{" "}
                                      {rep.evidence.map((e) => `${e.kind} ${e.id}`).join(", ")}
                                    </RowSub>
                                  ) : null}
                                </li>
                              ))}
                            </ul>
                          )}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </OpsBody>
    </OpsFrame>
  );
}
